import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FileText, Loader2, Plus, Search, X, Mic, Square, Sparkles, UserRound, Trash2, CopyPlus, Bookmark } from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { DescribeQuote } from "@/components/studio/DescribeQuote";
import { StatusBadge, EmptyState, ListSkeleton } from "@/components/studio/StudioUI";
import { useDictation } from "@/lib/useDictation";
import { studio, apiError, beDate, eur, inputCls, btnPrimary, TRADE_OPTIONS, JOB_TYPES, sym } from "@/lib/studioApi";

const emptyNew = { client_id: "", client_name: "", client_phone: "", client_email: "", site_city: "", trade: "", job_type: "autre", title: "", description: "", template_id: "" };

const FILTERS = [["tous", "Tous"], ["draft", "Brouillons"], ["attente", "En attente"], ["accepted", "Signés"], ["refused", "Refusés"]];
const DELETABLE = new Set(["draft", "refused", "expired", "cancelled"]);

export default function StudioQuotes() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [quotes, setQuotes] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(params.get("filtre") || "tous");
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(params.get("nouveau") === "1");
  const [form, setForm] = useState(emptyNew);
  const [useAi, setUseAi] = useState(true);
  const [busy, setBusy] = useState(false);
  const dictation = useDictation((text) => setForm((f) => ({ ...f, description: f.description ? `${f.description} ${text}` : text })));
  const [deleting, setDeleting] = useState("");
  const [duplicating, setDuplicating] = useState("");
  const duplicateQuote = async (q) => {
    setDuplicating(q.id);
    try {
      const { data } = await studio.post(`/quotes/${q.id}/duplicate`, {});
      toast.success(`Devis ${data.number} créé en brouillon à partir de ${q.number}.`);
      navigate(`/studio/devis/${data.id}`);
    } catch (e) { toast.error(apiError(e, "Duplication impossible.")); setDuplicating(""); }
  };
  const removeQuote = async (q) => {
    if (!window.confirm(q.status === "draft" ? `Supprimer le brouillon ${q.number} ?` : `Supprimer le devis ${q.number} ?`)) return;
    setDeleting(q.id);
    try { await studio.delete(`/quotes/${q.id}`); setQuotes((l) => l.filter((x) => x.id !== q.id)); toast.success(`${q.number} supprimé.`); }
    catch (e) { toast.error(apiError(e, "Suppression impossible.")); } finally { setDeleting(""); }
  };

  useEffect(() => {
    studio.get("/quotes").then(({ data }) => setQuotes(data)).catch(() => toast.error("Chargement impossible."));
    studio.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
    studio.get("/quote-templates").then(({ data }) => setTemplates(data)).catch(() => {});
  }, []);

  const removeTemplate = async (t) => {
    if (!window.confirm(`Supprimer le modèle « ${t.name} » ?`)) return;
    try { await studio.delete(`/quote-templates/${t.id}`); setTemplates((l) => l.filter((x) => x.id !== t.id)); setForm((f) => ({ ...f, template_id: "" })); toast.success("Modèle supprimé."); }
    catch (e) { toast.error(apiError(e, "Suppression impossible.")); }
  };
  const selectedTemplate = templates.find((t) => t.id === form.template_id);

  const withAi = useAi && !form.template_id && form.description.trim().length >= 12;

  const create = async () => {
    if (!form.client_id && !form.client_name.trim()) return toast.error("Choisissez un client ou indiquez son nom.");
    if (useAi && form.description.trim() && form.description.trim().length < 12) return toast.error("Décrivez les travaux en une phrase complète.");
    setBusy(true);
    try {
      if (withAi) {
        const { data } = await studio.post("/quotes/from-description", form, { timeout: 300000 });
        if (data.missing?.length) sessionStorage.setItem(`studio_warnings_${data.quote_id}`, JSON.stringify(data.missing));
        toast.success(data.template ? `Devis ${data.number} créé à partir de votre modèle « ${data.template} » (${data.lines.length} lignes), ajusté par l'IA.` : `Devis ${data.number} chiffré par l'IA : ${data.lines.length} ligne(s).`);
        navigate(`/studio/devis/${data.quote_id}`);
      } else {
        const { data } = await studio.post("/quotes/quick", form);
        toast.success(`Devis ${data.number} créé.`);
        navigate(`/studio/devis/${data.id}`);
      }
    } catch (e) { toast.error(apiError(e, "Création impossible.")); }
    finally { setBusy(false); }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (quotes || []).filter((quote) => {
      if (filter === "attente" ? !["sent", "viewed"].includes(quote.status) : filter !== "tous" && quote.status !== filter) return false;
      if (!q) return true;
      return [quote.number, quote.client_name, quote.site_city].some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [quotes, search, filter]);

  const counts = useMemo(() => (quotes || []).reduce((acc, q) => ({ ...acc, [q.status]: (acc[q.status] || 0) + 1 }), {}), [quotes]);
  const amounts = useMemo(() => (quotes || []).reduce((acc, q) => {
    const keys = ["tous", ["sent", "viewed"].includes(q.status) ? "attente" : q.status];
    keys.forEach((k) => { acc[k] = (acc[k] || 0) + Number(q.total || 0); });
    return acc;
  }, {}), [quotes]);
  const compact = (v) => (v >= 10000 ? `${Math.round(v / 1000)}k ${sym()}` : eur(v));
  const total = visible.reduce((sum, q) => sum + Number(q.total || 0), 0);

  return (
    <StudioLayout
      title="Devis"
      subtitle="Tous vos devis, du brouillon à la signature."
      action={
        <button type="button" data-testid="new-quote-btn" onClick={() => setOpen(!open)} className={btnPrimary}>
          {open ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {open ? "Fermer" : "Nouveau devis"}
        </button>
      }
    >
      {open ? (
        <div data-testid="new-quote-panel" className="dv-card-in mt-6 overflow-hidden rounded-2xl border border-[#E2E7F7] bg-white shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
          <div className="border-b border-[#E2E7F7] px-5 py-4 sm:px-6">
            <p className="font-heading text-base font-bold text-[#0F2645]">Nouveau devis</p>
            <p className="mt-1 text-sm text-[#5C6A87] leading-relaxed">
              Choisissez un client ou tapez son nom. Le dossier chantier est créé automatiquement.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 px-5 py-5 sm:grid-cols-2 sm:px-6">
            <select data-testid="new-quote-client" className={inputCls} value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}>
              <option value="">Nouveau client (à taper)</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{`${c.name || c.company}${c.city ? ` — ${c.city}` : ""}`}</option>)}
            </select>
            <input data-testid="new-quote-title" className={inputCls} placeholder="Intitulé du chantier (optionnel)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            {!form.client_id && (
              <>
                <input data-testid="new-quote-client-name" className={inputCls} placeholder="Nom du client *" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
                <input data-testid="new-quote-client-phone" className={inputCls} placeholder="Téléphone" value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} />
                <input data-testid="new-quote-client-email" type="email" className={inputCls} placeholder="Email (pour l'envoi et la signature)" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
                <input data-testid="new-quote-city" className={inputCls} placeholder="Ville du chantier" value={form.site_city} onChange={(e) => setForm({ ...form, site_city: e.target.value })} />
              </>
            )}
            <select data-testid="new-quote-trade" className={inputCls} value={form.trade} onChange={(e) => setForm({ ...form, trade: e.target.value, job_type: (JOB_TYPES[e.target.value] || JOB_TYPES.autre)[0][0] })}>
              <option value="">Mon métier par défaut</option>
              {TRADE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select data-testid="new-quote-job-type" className={inputCls} value={form.job_type} onChange={(e) => setForm({ ...form, job_type: e.target.value })}>
              {(JOB_TYPES[form.trade] || JOB_TYPES.autre).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          {/* ---------- Modèle de devis ---------- */}
          {templates.length > 0 && (
            <div data-testid="new-quote-template-block" className={`mx-5 mb-4 rounded-2xl border px-4 py-3.5 transition-colors sm:mx-6 ${form.template_id ? "border-[#3A5CC8]/40 bg-[#EEF1FC]/60" : "border-[#E2E7F7] bg-[#F6F8FD]"}`}>
              <p className="inline-flex items-center gap-1.5 font-heading text-sm font-bold text-[#0F2645]"><Bookmark className="h-4 w-4 text-[#3A5CC8]" /> Partir d'un modèle</p>
              <p className="mt-0.5 text-xs text-[#5C6A87]">Les lignes, prix et conditions du modèle sont repris ; vous ajustez ensuite les quantités.</p>
              <div className="mt-2 flex items-center gap-2">
                <select data-testid="new-quote-template" className={inputCls} value={form.template_id} onChange={(e) => { setForm({ ...form, template_id: e.target.value }); if (e.target.value) setUseAi(false); }}>
                  <option value="">Aucun modèle — devis vierge ou IA</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.lines_count} ligne{t.lines_count > 1 ? "s" : ""} · {eur(t.total)}{t.uses ? ` · utilisé ${t.uses}×` : ""}</option>)}
                </select>
                {selectedTemplate && (
                  <button type="button" data-testid="new-quote-template-delete" onClick={() => removeTemplate(selectedTemplate)} aria-label="Supprimer le modèle" title="Supprimer ce modèle" className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#E2E7F7] bg-white text-[#93A0BC] hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
              {selectedTemplate?.description && <p className="mt-2 text-xs text-[#5C6A87]">{selectedTemplate.description}</p>}
            </div>
          )}

          {/* ---------- Option IA ---------- */}
          {!form.template_id && (
          <div data-testid="new-quote-ai-block" className={`mx-5 mb-5 rounded-2xl border transition-colors duration-200 sm:mx-6 ${useAi ? "border-[#3A5CC8]/40 bg-[#EEF1FC]/60" : "border-[#E2E7F7] bg-[#F6F8FD]"}`}>
            <label className="flex cursor-pointer items-start gap-3 px-4 py-3.5">
              <input
                type="checkbox"
                data-testid="new-quote-ai-toggle"
                checked={useAi}
                onChange={(e) => setUseAi(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-[#C9D4F4] accent-[#3A5CC8]"
              />
              <span className="min-w-0 flex-1">
                <span className="inline-flex items-center gap-1.5 font-heading text-sm font-bold text-[#0F2645]">
                  <Sparkles className="h-4 w-4 text-[#3A5CC8]" /> Laisser l'IA chiffrer les lignes
                </span>
                <span className="mt-0.5 block text-xs leading-relaxed text-[#5C6A87]">
                  Décrivez les travaux en une phrase, à l'écrit ou à la voix : l'assistant propose les postes, quantités et prix depuis votre bibliothèque{templates.length ? ` et vos ${templates.length} modèle${templates.length > 1 ? "s" : ""} de devis (il choisit le plus proche)` : ""}. Vous validez tout avant envoi.
                </span>
              </span>
            </label>
            {useAi && (
              <div className="px-4 pb-4">
                <div className="relative">
                  <textarea
                    data-testid="new-quote-description"
                    rows={3}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Ex. : remplacement d'une chaudière gaz 24 kW, démontage de l'ancienne, raccordement, mise en service et évacuation."
                    className={`${inputCls} resize-none pr-14`}
                  />
                  <button
                    type="button"
                    data-testid="new-quote-mic"
                    disabled={dictation.transcribing || busy}
                    onClick={dictation.toggle}
                    aria-label={dictation.recording ? "Terminer la dictée" : "Dicter"}
                    className={`absolute right-2.5 top-2.5 inline-flex h-10 w-10 items-center justify-center rounded-xl transition-colors ${
                      dictation.recording ? "bg-rose-600 text-white animate-pulse" : "bg-[#0F2645] text-white hover:bg-[#3A5CC8]"
                    } disabled:opacity-40`}
                  >
                    {dictation.transcribing ? <Loader2 className="h-4 w-4 animate-spin" /> : dictation.recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                  </button>
                </div>
                <p className="mt-2 text-[11px] text-[#93A0BC]">
                  {dictation.recording ? "Enregistrement en cours… appuyez sur le carré pour terminer." : dictation.transcribing ? "Transcription en cours…" : "Laissez vide pour créer un devis vierge."}
                </p>
              </div>
            )}
          </div>
          )}

          <div className="flex flex-wrap items-center gap-3 border-t border-[#E2E7F7] bg-[#F6F8FD]/60 px-5 py-4 sm:px-6">
            <button type="button" data-testid="new-quote-submit" disabled={busy} onClick={create} className={btnPrimary}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : withAi ? <Sparkles className="w-4 h-4" /> : form.template_id ? <Bookmark className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
              {busy ? (withAi ? "Chiffrage en cours…" : "Création…") : withAi ? "Créer et chiffrer avec l'IA" : form.template_id ? "Créer depuis le modèle" : "Créer le devis"}
            </button>
            {withAi && <span className="text-xs text-[#5C6A87]">Quelques secondes : l'IA relit votre bibliothèque de prix.</span>}
          </div>
        </div>
      ) : (
        <DescribeQuote clients={clients} />
      )}

      <div data-testid="quotes-tabs" className="-mx-4 mt-6 flex gap-1 overflow-x-auto border-b border-[#E2E7F7] px-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0">
        {FILTERS.map(([v, l]) => {
          const n = v === "tous" ? (quotes || []).length : v === "attente" ? (counts.sent || 0) + (counts.viewed || 0) : counts[v] || 0;
          const active = filter === v;
          return (
            <button
              key={v}
              type="button"
              data-testid={`quotes-filter-${v}`}
              onClick={() => setFilter(v)}
              className={`-mb-px flex shrink-0 flex-col items-start gap-0.5 border-b-2 px-3 pb-2.5 pt-1 text-left transition-colors duration-200 ${active ? "border-[#3A5CC8]" : "border-transparent hover:border-[#C7D2F5]"}`}
            >
              <span className={`inline-flex items-center gap-1.5 text-sm font-semibold ${active ? "text-[#3A5CC8]" : "text-[#0F2645]"}`}>
                {l}
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${active ? "bg-[#EEF1FC] text-[#3A5CC8]" : "bg-[#F3F5FC] text-[#5C6A87]"}`}>{n}</span>
              </span>
              <span className={`font-mono text-xs tabular-nums ${active ? "text-[#3A5CC8]" : "text-[#5C6A87]"}`}>{compact(amounts[v] || 0)}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#93A0BC]" />
        <input data-testid="quotes-search" className={`${inputCls} pl-9 bg-[#F6F8FD]`} placeholder="Rechercher un devis…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <p className="mt-3 text-sm text-[#5C6A87]">
        {visible.length} devis · <span className="font-mono font-bold text-[#0F2645]">{eur(total)}</span> TVAC
      </p>

      <div data-testid="quotes-list" className="mt-3 overflow-hidden rounded-2xl border border-[#E2E7F7] bg-white shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
        {quotes === null && <ListSkeleton />}
        {visible.map((q, i) => (
          <div
            key={q.id}
            data-testid={`quote-row-${q.number}`}
            style={{ animationDelay: `${Math.min(i, 8) * 30}ms` }}
            className="dv-card-in group flex items-stretch border-b border-[#EEF1FC] transition-colors last:border-b-0 hover:bg-[#F6F8FD]"
          >
            <Link to={`/studio/devis/${q.id}`} data-testid={`quote-open-${q.number}`} className="min-w-0 flex-1 px-4 py-3.5 sm:px-5">
              <div className="flex items-center justify-between gap-3">
                <p className="font-heading text-sm font-extrabold text-[#0F2645]">{q.number}</p>
                <p className="shrink-0 font-mono text-sm font-bold tabular-nums text-[#0F2645]">{eur(q.total)} <span className="text-[11px] font-semibold text-[#93A0BC]">TVAC</span></p>
              </div>
              <div className="mt-1.5 flex items-center justify-between gap-3">
                <p className="flex min-w-0 items-center gap-2 text-sm text-[#0F2645]">
                  <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#E2E7F7] text-[#3A5CC8]"><UserRound className="h-3.5 w-3.5" strokeWidth={2.2} /></span>
                  <span className="truncate">{q.client_name}{q.site_city ? <span className="text-[#93A0BC]"> · {q.site_city}</span> : null}</span>
                </p>
                <span className="flex items-center gap-2">
                  {q.status === "draft" && <span className="hidden text-[11px] font-semibold text-[#3A5CC8] sm:inline">Reprendre →</span>}
                  <StatusBadge value={q.status} />
                </span>
              </div>
            </Link>
            <button
              type="button"
              data-testid={`quote-duplicate-${q.number}`}
              disabled={duplicating === q.id}
              onClick={() => duplicateQuote(q)}
              aria-label="Dupliquer le devis"
              title="Dupliquer en nouveau brouillon"
              className="flex w-12 shrink-0 items-center justify-center border-l border-[#EEF1FC] text-[#93A0BC] transition-colors hover:bg-[#EEF1FC] hover:text-[#3A5CC8] disabled:opacity-40 sm:w-14"
            >
              {duplicating === q.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CopyPlus className="h-4 w-4" />}
            </button>
            {DELETABLE.has(q.status) && (
              <button
                type="button"
                data-testid={`quote-delete-${q.number}`}
                disabled={deleting === q.id}
                onClick={() => removeQuote(q)}
                aria-label="Supprimer le devis"
                title={q.status === "draft" ? "Supprimer ce brouillon" : "Supprimer ce devis"}
                className="flex w-12 shrink-0 items-center justify-center border-l border-[#EEF1FC] text-[#93A0BC] transition-colors hover:bg-rose-50 hover:text-rose-600 disabled:opacity-40 sm:w-14"
              >
                {deleting === q.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              </button>
            )}
          </div>
        ))}
        {quotes !== null && !visible.length && (
          <EmptyState
            testId="quotes-empty"
            icon={FileText}
            title={quotes.length ? "Aucun devis ne correspond" : "Aucun devis pour l'instant"}
            hint={quotes.length ? "Modifiez la recherche ou le filtre." : "Décrivez votre chantier ci-dessus : l'IA prépare le premier devis en quelques secondes."}
            action={!quotes.length && (
              <button type="button" data-testid="quotes-empty-new" onClick={() => setOpen(true)} className={`${btnPrimary} mt-2`}>
                <Plus className="h-4 w-4" /> Nouveau devis
              </button>
            )}
          />
        )}
      </div>
    </StudioLayout>
  );
}
